import { useCallback, useRef, useState } from 'react';
import { useUploadFile } from 'api/assets/assetsServices';
import type { AxiosProgressEvent } from 'axios';
import {
  ALLOWED_FILE_TYPES,
  FILE_UPLOAD_CONFIG,
  FILE_UPLOAD_STATUS,
  formatFileSize,
  generateFileKey,
  isValidFileType,
} from 'lib/fileUploadConstants';

export interface FileUploadItem {
  file: File;
  key: string;
  progress: number;
  status: (typeof FILE_UPLOAD_STATUS)[keyof typeof FILE_UPLOAD_STATUS];
  error?: string;
}

interface UseFileUploadProps {
  assetId: string | number;
  allowedTypes?: readonly string[];
  maxFileSize?: number;
  maxFiles?: number;
  onUploadSuccess?: (file: File) => void;
  onUploadError?: (file: File, error: string) => void;
}

interface UseFileUploadReturn {
  uploadItems: FileUploadItem[];
  addFiles: (files: File[]) => void;
  retryUpload: (fileKey: string) => void;
  removeFile: (fileKey: string) => void;
  clearAll: () => void;
  cancelUpload: (fileKey: string) => void;
  isUploading: boolean;
  hasErrors: boolean;
  completedCount: number;
  totalCount: number;
}

export const useFileUpload = ({
  assetId,
  allowedTypes = ALLOWED_FILE_TYPES.ALL,
  maxFileSize = FILE_UPLOAD_CONFIG.MAX_FILE_SIZE,
  maxFiles = FILE_UPLOAD_CONFIG.MAX_FILES_COUNT,
  onUploadSuccess,
  onUploadError,
}: UseFileUploadProps): UseFileUploadReturn => {
  const [uploadItems, setUploadItems] = useState<FileUploadItem[]>([]);
  const { mutateAsync: uploadFile } = useUploadFile();

  const uploadSingleFileRef = useRef<(file: File) => Promise<void>>();

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      if (file.size > maxFileSize) {
        return `File size exceeds ${formatFileSize(maxFileSize)} limit`;
      }

      // Check file type
      if (!isValidFileType(file.name, allowedTypes)) {
        return `File type not allowed. Allowed types: ${allowedTypes.join(
          ', '
        )}`;
      }

      return null;
    },
    [maxFileSize, allowedTypes]
  );

  const addFiles = useCallback(
    (newFiles: File[]): void => {
      const validFiles: File[] = [];
      const errors: { file: File; error: string }[] = [];

      // Check total file count
      if (uploadItems.length + newFiles.length > maxFiles) {
        errors.push({
          file: newFiles[0],
          error: `Cannot upload more than ${maxFiles} files`,
        });
        return;
      }

      // Validate each file
      for (const file of newFiles) {
        const existingFile = uploadItems.find(
          (item) => item.key === generateFileKey(file)
        );

        if (existingFile) {
          errors.push({ file, error: 'File already exists' });
          continue;
        }

        const validationError = validateFile(file);
        if (validationError) {
          errors.push({ file, error: validationError });
          continue;
        }

        validFiles.push(file);
      }

      // Add valid files to upload queue
      if (validFiles.length > 0) {
        const newUploadItems: FileUploadItem[] = validFiles.map((file) => ({
          file,
          key: generateFileKey(file),
          progress: 0,
          status: FILE_UPLOAD_STATUS.PENDING,
        }));

        setUploadItems((prev) => [...prev, ...newUploadItems]);

        // Start uploading valid files
        validFiles.forEach((file) => {
          void uploadSingleFileRef.current?.(file);
        });
      }

      // Handle errors
      errors.forEach(({ file, error }) => {
        onUploadError?.(file, error);
      });
    },
    [uploadItems, maxFiles, validateFile, onUploadError]
  );

  const uploadSingleFile = useCallback(
    async (file: File): Promise<void> => {
      const fileKey = generateFileKey(file);

      try {
        // Update status to uploading
        setUploadItems((prev) =>
          prev.map((item) =>
            item.key === fileKey
              ? { ...item, status: FILE_UPLOAD_STATUS.UPLOADING }
              : item
          )
        );

        await uploadFile({
          file,
          onUploadProgress: (event: AxiosProgressEvent) => {
            const percent =
              event?.total && event.total > 0
                ? Math.round((event.loaded * 95) / event.total)
                : 0;

            setUploadItems((prev) =>
              prev.map((item) =>
                item.key === fileKey ? { ...item, progress: percent } : item
              )
            );
          },
          assetId,
        });

        // Mark as successful
        setUploadItems((prev) =>
          prev.map((item) =>
            item.key === fileKey
              ? {
                  ...item,
                  status: FILE_UPLOAD_STATUS.SUCCESS,
                  progress: 100,
                }
              : item
          )
        );

        onUploadSuccess?.(file);
      } catch (error: any) {
        const errorMessage = error?.message || 'Upload failed';

        setUploadItems((prev) =>
          prev.map((item) =>
            item.key === fileKey
              ? {
                  ...item,
                  status: FILE_UPLOAD_STATUS.ERROR,
                  error: errorMessage,
                }
              : item
          )
        );

        onUploadError?.(file, errorMessage);
      }
    },
    [uploadFile, assetId, onUploadSuccess, onUploadError]
  );

  // Assign the function to the ref to avoid circular dependency issues
  uploadSingleFileRef.current = uploadSingleFile;

  const retryUpload = useCallback(
    (fileKey: string): void => {
      const uploadItem = uploadItems.find((item) => item.key === fileKey);
      if (uploadItem) {
        setUploadItems((prev) =>
          prev.map((item) =>
            item.key === fileKey
              ? {
                  ...item,
                  status: FILE_UPLOAD_STATUS.PENDING,
                  progress: 0,
                  error: undefined,
                }
              : item
          )
        );

        void uploadSingleFileRef.current?.(uploadItem.file);
      }
    },
    [uploadItems]
  );

  const removeFile = useCallback((fileKey: string): void => {
    setUploadItems((prev) => prev.filter((item) => item.key !== fileKey));
  }, []);

  const clearAll = useCallback((): void => {
    setUploadItems([]);
  }, []);

  const cancelUpload = useCallback((fileKey: string): void => {
    setUploadItems((prev) =>
      prev.map((item) =>
        item.key === fileKey
          ? { ...item, status: FILE_UPLOAD_STATUS.CANCELLED }
          : item
      )
    );
  }, []);

  return {
    uploadItems,
    addFiles,
    retryUpload,
    removeFile,
    clearAll,
    cancelUpload,
    isUploading: uploadItems.some(
      (item) => item.status === FILE_UPLOAD_STATUS.UPLOADING
    ),
    hasErrors: uploadItems.some(
      (item) => item.status === FILE_UPLOAD_STATUS.ERROR
    ),
    completedCount: uploadItems.filter(
      (item) => item.status === FILE_UPLOAD_STATUS.SUCCESS
    ).length,
    totalCount: uploadItems.length,
  };
};
